/**
 * useOfflineDevis — cache hors-ligne pour le Devis Intelligent
 *
 * Fonctionnement :
 *  • Quand l'appareil est en ligne  → save() écrit dans Supabase + localStorage (cache)
 *  • Quand l'appareil est hors-ligne → save() écrit uniquement dans localStorage (draft)
 *  • Au retour du réseau            → sync() envoie automatiquement tous les drafts
 *
 * Clé localStorage : "yoma_devis_drafts"
 * Format : Array<{ id, cabinetId, rows, meta, savedAt, synced }>
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { onNetworkChange, getNetworkStatus } from '../lib/capacitorShim';

const STORAGE_KEY = 'yoma_devis_drafts';

function readDrafts() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeDrafts(drafts) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch (e) {
    console.warn('[useOfflineDevis] localStorage full:', e);
  }
}

function generateId() {
  return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function useOfflineDevis(cabinetId) {
  const [isOnline,    setIsOnline]    = useState(navigator.onLine);
  const [drafts,      setDrafts]      = useState(readDrafts);
  const [syncStatus,  setSyncStatus]  = useState(null); // null | 'syncing' | 'synced' | 'error'
  const syncLockRef = useRef(false);

  // ── Écoute réseau ──────────────────────────────────────────────────────────
  useEffect(() => {
    getNetworkStatus().then(s => setIsOnline(s.connected));
    const unsub = onNetworkChange(status => {
      setIsOnline(status.connected);
      if (status.connected) syncPendingDrafts();
    });
    return unsub;
  }, []); // eslint-disable-line

  // ── Synchroniser les drafts en attente ────────────────────────────────────
  const syncPendingDrafts = useCallback(async () => {
    if (syncLockRef.current) return;
    const pending = readDrafts().filter(d => !d.synced);
    if (!pending.length) return;

    syncLockRef.current = true;
    setSyncStatus('syncing');

    let successCount = 0;
    const allDrafts = readDrafts();

    for (const draft of pending) {
      try {
        const { error } = await supabase
          .from('devis')
          .upsert({
            id:          draft.id.startsWith('draft_') ? undefined : draft.id,
            cabinet_id:  draft.cabinetId,
            nom_projet:  draft.meta?.nomProjet || '',
            nom_client:  draft.meta?.nomClient || '',
            adresse:     draft.meta?.adresse || '',
            rows:        draft.rows,
            created_at:  draft.savedAt,
            updated_at:  new Date().toISOString(),
          });

        if (!error) {
          const idx = allDrafts.findIndex(d => d.id === draft.id);
          if (idx !== -1) allDrafts[idx].synced = true;
          successCount++;
        }
      } catch (e) {
        console.warn('[useOfflineDevis] sync error for draft', draft.id, e);
      }
    }

    writeDrafts(allDrafts);
    setDrafts([...allDrafts]);
    setSyncStatus(successCount > 0 ? 'synced' : 'error');
    syncLockRef.current = false;

    // Effacer le badge après 3s
    setTimeout(() => setSyncStatus(null), 3000);
  }, []);

  // ── Sauvegarder un devis ──────────────────────────────────────────────────
  const saveDevis = useCallback(async ({ rows, meta, existingId }) => {
    const draft = {
      id:        existingId || generateId(),
      cabinetId: cabinetId,
      rows,
      meta,
      savedAt:   new Date().toISOString(),
      synced:    false,
    };

    // Toujours écrire dans localStorage en premier (sauvegarde instantanée)
    const allDrafts = readDrafts();
    const existingIdx = allDrafts.findIndex(d => d.id === draft.id);
    if (existingIdx !== -1) {
      allDrafts[existingIdx] = draft;
    } else {
      allDrafts.unshift(draft);
      // Garder max 50 drafts
      if (allDrafts.length > 50) allDrafts.splice(50);
    }
    writeDrafts(allDrafts);
    setDrafts([...allDrafts]);

    // Si en ligne → tenter sync immédiat
    if (isOnline) {
      try {
        const { data, error } = await supabase
          .from('devis')
          .upsert({
            id:          existingId && !existingId.startsWith('draft_') ? existingId : undefined,
            cabinet_id:  cabinetId,
            nom_projet:  meta?.nomProjet || '',
            nom_client:  meta?.nomClient || '',
            adresse:     meta?.adresse || '',
            rows,
            updated_at:  draft.savedAt,
          })
          .select('id')
          .single();

        if (!error) {
          // Marquer synced + enregistrer l'ID Supabase réel
          const updated = readDrafts().map(d =>
            d.id === draft.id ? { ...d, id: data?.id || d.id, synced: true } : d
          );
          writeDrafts(updated);
          setDrafts(updated);
          return { success: true, id: data?.id || draft.id, offline: false };
        }
      } catch (_) {}
    }

    return { success: true, id: draft.id, offline: true };
  }, [cabinetId, isOnline]);

  // ── Supprimer un draft local ──────────────────────────────────────────────
  const removeDraft = useCallback((id) => {
    const updated = readDrafts().filter(d => d.id !== id);
    writeDrafts(updated);
    setDrafts(updated);
  }, []);

  // ── Charger les devis depuis Supabase (si en ligne) ───────────────────────
  const fetchRemoteDevis = useCallback(async () => {
    if (!isOnline || !cabinetId) return [];
    const { data, error } = await supabase
      .from('devis')
      .select('*')
      .eq('cabinet_id', cabinetId)
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) return [];
    return data || [];
  }, [isOnline, cabinetId]);

  const unsyncedCount = drafts.filter(d => !d.synced).length;

  return {
    isOnline,
    drafts,
    unsyncedCount,
    syncStatus,
    saveDevis,
    removeDraft,
    syncPendingDrafts,
    fetchRemoteDevis,
  };
}
